import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { orderApi, reservationApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const C = {
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
  text1:'#1a0f00', text2:'#3d2010', text3:'#6b4c35', text4:'#a07858',
  border:'#f0e4d8', border2:'#f5ede4', card:'#ffffff', surface:'#fffaf7',
  green:'#16a34a', greenL:'#dcfce7',
};

const STATUS_COLORS = {
  pending:   { bg:'#fef9c3', color:'#854d0e', border:'#fde68a' },
  confirmed: { bg:'#dbeafe', color:'#1e40af', border:'#bfdbfe' },
  preparing: { bg:'#ffedd5', color:'#9a3412', border:'#fed7aa' },
  ready:     { bg:'#f0fdf4', color:'#15803d', border:'#bbf7d0' },
  delivered: { bg:'#dcfce7', color:'#166534', border:'#a5d6a7' },
  cancelled: { bg:'#fee2e2', color:'#991b1b', border:'#fca5a5' },
};

const STATUS_FLOW = ['pending','confirmed','preparing','ready','delivered'];

const StaffDashboard = () => {
  const { slug } = useParams();
  const { admin, logout } = useAuth();
  const [orders, setOrders]           = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState('orders');
  const [updating, setUpdating]       = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [filter, setFilter]           = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderItems, setOrderItems]       = useState({});
  const [loadingItems, setLoadingItems]   = useState(null);

  const fetchData = async () => {
    try {
      const [oRes, rRes] = await Promise.all([
        orderApi.getAll({ limit: 50 }),
        reservationApi.getAll({ status: 'confirmed' }),
      ]);
      setOrders(oRes.data.data || []);
      setReservations(rRes.data.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOrderStatus = async (id, status) => {
    setUpdating(id);
    try {
      await orderApi.updateStatus(id, status);
      toast.success('Order updated to ' + status);
      fetchData();
    } catch { toast.error('Update failed'); }
    finally { setUpdating(null); }
  };

  const handleReservationStatus = async (id, status) => {
    setUpdating(id);
    try {
      await reservationApi.updateStatus(id, status);
      toast.success('Reservation ' + status);
      fetchData();
    } catch { toast.error('Update failed'); }
    finally { setUpdating(null); }
  };

  const toggleItems = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
      return;
    }
    setExpandedOrder(orderId);
    if (orderItems[orderId]) return;
    setLoadingItems(orderId);
    try {
      const res = await orderApi.getOne(orderId);
      const items = res.data.data?.items || [];
      setOrderItems(prev => ({ ...prev, [orderId]: items }));
    } catch {
      toast.error('Items load nahi ho sake');
    } finally {
      setLoadingItems(null);
    }
  };

  const filteredOrders = filter
    ? orders.filter(o => o.status === filter)
    : orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled');

  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const readyCount   = orders.filter(o => o.status === 'ready').length;

  return (
    <div>
      {/* Cancel Confirmation Popup */}
      {cancelConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:16, padding:32, maxWidth:360, width:'90%', textAlign:'center', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize:48, marginBottom:16 }}>⚠️</div>
            <h3 style={{ fontSize:18, fontWeight:700, color:'#1f2937', marginBottom:8 }}>Cancel Order?</h3>
            <p style={{ color:'#6b7280', marginBottom:24 }}>Kya aap sure hain? Ye order cancel ho jaayega.</p>
            <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
              <button onClick={()=>setCancelConfirm(null)} style={{ padding:'10px 24px', borderRadius:10, border:'1px solid #e5e7eb', background:'white', color:'#374151', fontWeight:600, cursor:'pointer', fontSize:14 }}>
                Nahi, Wapas Jao
              </button>
              <button onClick={()=>{ handleOrderStatus(cancelConfirm,'cancelled'); setCancelConfirm(null); }} style={{ padding:'10px 24px', borderRadius:10, border:'none', background:'#ef4444', color:'white', fontWeight:600, cursor:'pointer', fontSize:14 }}>
                Haan, Cancel Karo
              </button>
            </div>
          </div>
        </div>
      )}
    <div style={{ minHeight:'100vh', background:'#fdf8f4' }}>

      <div style={{
        background: 'linear-gradient(135deg, #1a0f00, #0e0700)',
        padding: '16px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{
            width:40, height:40, borderRadius:12,
            background: 'linear-gradient(135deg,#c96428,#9a4a1e)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:18, fontWeight:800, color:'white',
          }}>
            {admin?.name?.[0]?.toUpperCase() || 'S'}
          </div>
          <div>
            <p style={{ color:'#f5ede0', fontWeight:700, fontSize:15, margin:0 }}>{admin?.name}</p>
            <p style={{ color:'#c96428', fontSize:11, margin:0, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Staff</p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={fetchData}
            style={{ padding:'8px 16px', borderRadius:10, border:'1px solid rgba(201,100,40,0.3)', background:'rgba(201,100,40,0.1)', color:'#e8a878', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
            ↻ Refresh
          </button>
          <button onClick={()=>logout(slug)}
            style={{ padding:'8px 16px', borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#6b4226', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
            Sign Out
          </button>
        </div>
      </div>

      {(pendingCount > 0 || readyCount > 0) && (
        <div style={{ background:'#fff8f3', borderBottom:'1px solid ' + C.border, padding:'10px 24px', display:'flex', gap:12 }}>
          {pendingCount > 0 && (
            <span style={{ padding:'6px 14px', borderRadius:99, background:'#fef9c3', color:'#854d0e', border:'1px solid #fde68a', fontSize:12, fontWeight:700 }}>
              ⏳ {pendingCount} Pending
            </span>
          )}
          {readyCount > 0 && (
            <motion.span animate={{ scale:[1,1.05,1] }} transition={{ repeat:Infinity, duration:1.5 }}
              style={{ padding:'6px 14px', borderRadius:99, background:C.greenL, color:C.green, border:'1px solid #a5d6a7', fontSize:12, fontWeight:700 }}>
              🛎️ {readyCount} Ready to Serve!
            </motion.span>
          )}
        </div>
      )}

      <div style={{ maxWidth:900, margin:'0 auto', padding:'24px 20px' }}>

        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['orders','📦 Orders'],['reservations','📅 Reservations']].map(([key,label])=>(
            <button key={key} onClick={()=>setTab(key)}
              style={{
                padding:'10px 20px', borderRadius:12, border:'none', cursor:'pointer',
                fontFamily:'inherit', fontSize:14, fontWeight:700,
                background: tab===key ? 'linear-gradient(135deg,#c96428,#9a4a1e)' : C.card,
                color: tab===key ? 'white' : C.text3,
                border: tab===key ? 'none' : '1px solid ' + C.border,
                boxShadow: tab===key ? '0 4px 14px rgba(201,100,40,0.3)' : 'none',
              }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'orders' && (
          <div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
              {[['','Active'],['pending','Pending'],['confirmed','Confirmed'],['preparing','Preparing'],['ready','Ready'],['delivered','Delivered'],['cancelled','Cancelled']].map(([s,label])=>(
                <button key={s} onClick={()=>setFilter(s)}
                  style={{
                    padding:'6px 14px', borderRadius:99, fontSize:12, fontWeight:600,
                    fontFamily:'inherit', cursor:'pointer',
                    background: filter===s ? C.accent : C.card,
                    color: filter===s ? 'white' : C.text3,
                    border: '1px solid ' + (filter===s ? C.accent : C.border),
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {loading ? (
              <p style={{ color:C.text4, textAlign:'center', padding:'40px 0' }}>Loading orders...</p>
            ) : filteredOrders.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 24px' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                <p style={{ color:C.text3, fontSize:16, fontWeight:600 }}>All clear! No active orders.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {filteredOrders.map(order => {
                  const sc = STATUS_COLORS[order.status] || STATUS_COLORS.pending;
                  const currentStep = STATUS_FLOW.indexOf(order.status);
                  const nextStatus = STATUS_FLOW[currentStep + 1];
                  const isUpdating = updating === order.id;
                  const isExpanded = expandedOrder === order.id;
                  const items = orderItems[order.id] || [];
                  const isLoadingThisItem = loadingItems === order.id;

                  return (
                    <motion.div key={order.id}
                      initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                      style={{
                        background:C.card, borderRadius:16, overflow:'hidden',
                        border: order.status==='ready' ? '2px solid ' + C.green : '1px solid ' + C.border,
                        boxShadow: order.status==='ready' ? '0 4px 20px ' + C.green + '20' : '0 2px 8px rgba(0,0,0,0.04)',
                      }}>

                      <div style={{ padding:'18px 20px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, flexWrap:'wrap' }}>
                              <span style={{ fontFamily:'monospace', fontWeight:800, color:C.accent, fontSize:16 }}>#{order.order_number || order.id}</span>
                              <span style={{ fontWeight:700, color:C.text1, fontSize:15, textTransform:'capitalize' }}>{order.customer_name}</span>
                              {order.table_number && (
                                <span style={{ background:'#f0fdf4', color:'#15803d', borderRadius:99, padding:'2px 8px', fontSize:11, fontWeight:700, border:'1px solid #a5d6a7' }}>
                                  🪑 {order.table_number}
                                </span>
                              )}
                              <span style={{ fontSize:11, padding:'3px 10px', borderRadius:99, background:sc.bg, color:sc.color, border:'1px solid ' + sc.border, fontWeight:700, textTransform:'capitalize' }}>
                                {order.status}
                              </span>
                            </div>

                            <button
                              onClick={() => toggleItems(order.id)}
                              style={{
                                display:'inline-flex', alignItems:'center', gap:6,
                                padding:'5px 12px', borderRadius:99, border:'1px solid ' + C.accentB,
                                background: isExpanded ? C.accentL : '#faf7f4',
                                color: C.accent, fontSize:12, fontWeight:600,
                                cursor:'pointer', fontFamily:'inherit', marginBottom:6,
                              }}>
                              {isLoadingThisItem ? (
                                <span style={{ width:10, height:10, border:'2px solid ' + C.accentB, borderTopColor:C.accent, borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}/>
                              ) : (
                                <span style={{ fontSize:10, display:'inline-block', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.2s' }}>▼</span>
                              )}
                              🍽️ {order.item_count} item(s) · {(order.order_type||'dine-in').replace('-',' ')} · ₹{parseFloat(order.total_amount).toFixed(0)}
                            </button>

                            <p style={{ color:C.text4, fontSize:11, margin:0 }}>
                              {new Date(order.created_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true})}
                            </p>
                          </div>

                          <div style={{ display:'flex', flexDirection:'column', gap:8, minWidth:140 }}>
                            {nextStatus && order.status !== 'delivered' && order.status !== 'cancelled' && (
                              <button onClick={()=>handleOrderStatus(order.id, nextStatus)} disabled={isUpdating}
                                style={{
                                  padding:'10px 16px', borderRadius:10, border:'none', cursor:'pointer',
                                  background: order.status==='ready' ? 'linear-gradient(135deg,' + C.green + ',#15803d)' : 'linear-gradient(135deg,#c96428,#9a4a1e)',
                                  color:'white', fontWeight:700, fontSize:12, fontFamily:'inherit',
                                  opacity: isUpdating ? 0.7 : 1,
                                }}>
                                {isUpdating ? '...' : nextStatus === 'delivered' ? '✅ Mark Delivered' : '→ ' + nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                              </button>
                            )}
                            {order.status !== 'cancelled' && order.status !== 'delivered' && (
                              <button onClick={()=>setCancelConfirm(order.id)} disabled={isUpdating}
                                style={{
                                  padding:'8px 16px', borderRadius:10, border:'1px solid #fca5a5',
                                  background:'#fff5f5', color:'#ef4444', fontWeight:600, fontSize:11,
                                  cursor:'pointer', fontFamily:'inherit',
                                }}>
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height:0, opacity:0 }}
                            animate={{ height:'auto', opacity:1 }}
                            exit={{ height:0, opacity:0 }}
                            transition={{ duration:0.2 }}
                            style={{ overflow:'hidden' }}>
                            <div style={{
                              margin:'0 16px 16px',
                              borderRadius:12, overflow:'hidden',
                              border:'1px solid ' + C.accentB,
                              background: C.accentL,
                            }}>
                              <div style={{
                                padding:'8px 14px',
                                background: 'linear-gradient(135deg, #c96428, #9a4a1e)',
                                display:'grid', gridTemplateColumns:'2fr 1fr 1fr',
                              }}>
                                <span style={{ color:'white', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>Item</span>
                                <span style={{ color:'white', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, textAlign:'center' }}>Qty × Price</span>
                                <span style={{ color:'white', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, textAlign:'right' }}>Subtotal</span>
                              </div>

                              {isLoadingThisItem ? (
                                <div style={{ padding:'16px', textAlign:'center', color:C.text4, fontSize:13 }}>Loading items...</div>
                              ) : items.length === 0 ? (
                                <div style={{ padding:'16px', textAlign:'center', color:C.text4, fontSize:13 }}>Items nahi mile</div>
                              ) : (
                                <>
                                  {items.map((item, idx) => (
                                    <div key={idx} style={{
                                      display:'grid', gridTemplateColumns:'2fr 1fr 1fr',
                                      alignItems:'center',
                                      padding:'10px 14px',
                                      borderBottom: idx < items.length - 1 ? '1px solid ' + C.accentB : 'none',
                                      background: idx % 2 === 0 ? 'transparent' : 'rgba(201,100,40,0.04)',
                                    }}>
                                      <span style={{ fontWeight:600, color:C.text1, fontSize:13 }}>{item.item_name}</span>
                                      <span style={{ color:C.text3, fontSize:13, textAlign:'center' }}>
                                        {item.quantity} × ₹{parseFloat(item.price).toFixed(0)}
                                      </span>
                                      <span style={{ fontWeight:700, color:C.accent, fontSize:13, textAlign:'right' }}>
                                        ₹{parseFloat(item.subtotal).toFixed(0)}
                                      </span>
                                    </div>
                                  ))}

                                  {order.notes && (
                                    <div style={{ padding:'8px 14px', borderTop:'1px solid ' + C.accentB, background:'rgba(201,100,40,0.06)' }}>
                                      <span style={{ fontSize:11, color:C.text3, fontWeight:600 }}>📝 Note: </span>
                                      <span style={{ fontSize:11, color:C.text2 }}>{order.notes}</span>
                                    </div>
                                  )}

                                  <div style={{
                                    display:'flex', justifyContent:'space-between', alignItems:'center',
                                    padding:'10px 14px',
                                    borderTop:'1.5px solid ' + C.accentB,
                                    background:'rgba(201,100,40,0.08)',
                                  }}>
                                    <span style={{ fontWeight:700, color:C.text1, fontSize:13 }}>Total</span>
                                    <span style={{ fontWeight:800, color:C.accent, fontSize:15 }}>
                                      ₹{parseFloat(order.total_amount).toFixed(0)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'reservations' && (
          <div>
            {loading ? (
              <p style={{ color:C.text4, textAlign:'center', padding:'40px 0' }}>Loading...</p>
            ) : reservations.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 24px' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
                <p style={{ color:C.text3, fontSize:16, fontWeight:600 }}>No confirmed reservations today.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {reservations.map(r => (
                  <motion.div key={r.id} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}
                    style={{ background:C.card, borderRadius:16, padding:'18px 20px', border:'1px solid ' + C.border, boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <p style={{ fontWeight:700, color:C.text1, fontSize:15, margin:'0 0 4px', textTransform:'capitalize' }}>{r.name}</p>
                        <p style={{ color:C.text3, fontSize:13, margin:'0 0 2px' }}>📞 {r.phone} · 👥 {r.guests} guests</p>
                        <p style={{ color:C.accent, fontWeight:700, fontSize:13, margin:'0 0 2px' }}>
                          📅 {new Date(r.reservation_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} at {r.reservation_time}
                        </p>
                        {r.special_requests && <p style={{ color:C.text4, fontSize:12, margin:'4px 0 0' }}>📝 {r.special_requests}</p>}
                        {r.pre_order_items && r.pre_order_items.length > 0 && (
                          <div style={{ marginTop:8, padding:'8px 12px', borderRadius:10, background:C.accentL, border:'1px solid ' + C.accentB }}>
                            <p style={{ color:C.accent, fontWeight:700, fontSize:11, marginBottom:4 }}>🍽️ Pre-ordered Items:</p>
                            {r.pre_order_items.map((item,i) => (
                              <p key={i} style={{ color:C.text2, fontSize:12, margin:'2px 0' }}>• {item.item_name} × {item.quantity} — ₹{item.subtotal?.toFixed(0)}</p>
                            ))}
                            <p style={{ color:C.accent, fontWeight:700, fontSize:12, marginTop:4 }}>Total: ₹{parseFloat(r.pre_order_total||0).toFixed(0)}</p>
                          </div>
                        )}
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <button onClick={()=>handleReservationStatus(r.id,'cancelled')} disabled={updating===r.id}
                          style={{ padding:'8px 14px', borderRadius:10, border:'1px solid #fca5a5', background:'#fff5f5', color:'#ef4444', fontWeight:600, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
    </div>
  );
};

export default StaffDashboard;
